/**
 * Template Review Traveller Slice
 *
 * Manages mutable UI state for the flights-review-travellers template.
 *
 * **Architecture**:
 * - Template data (immutable) is passed via props from TemplatePane
 * - This slice only stores mutable UI state for user interactions
 *
 * **uiState** (mutable): Stores user interactions and derived state.
 *   - savedTravellers, savedContactDetails, savedGstDetails (user edits)
 *   - fareSummary (updates on addon selection)
 *   - isTravellerFormValidated (form validation status)
 *
 * Note: selectedFares and selectedFareCombo are in templateInterstitialSlice.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  TravellerItem,
  PaxType,
  SavedTravellersState,
  ContactDetailsFormValues,
  GstDetailsFormValues,
  FlightsReviewTravellersTemplateData,
} from '@contracts';
import type { FareSummary } from '../types/flightsTypes';
import { validateTravellerFormThunk } from '../thunks/reviewTravellerThunks';
import {
  selectAddonThunk,
  selectMealThunk,
  selectBaggageThunk,
  selectCouponThunk,
} from '../thunks/addonsThunks';
import {
  EMPTY_CONTACT_DETAILS,
  EMPTY_GST_DETAILS,
  updateFareSummaryFromResponse,
  initializeUiStateFromTemplateData,
} from '../utils/reviewStateHelpers';
import {
  setActiveTemplate,
  updateTemplateFromStream,
} from '@/features/chat/redux/slices/templateSlice';

// ============================================
// State Types
// ============================================

/**
 * UI State - Mutable state that changes based on user interactions
 * Initialized from template data (passed via props), mutated by user actions
 */
export interface ReviewTravellerUiState {
  /**
   * Saved travellers state - persists across navigation
   * Maps pax type (ADULT, CHILD, INFANT) to array of TravellerItem
   * Initialized from template data, mutated by user
   */
  savedTravellers: SavedTravellersState;
  /**
   * Saved contact details - persists across navigation
   * Initialized from template data, mutated by user
   */
  savedContactDetails: ContactDetailsFormValues;
  /**
   * Saved GST details - persists across navigation
   * Initialized from template data, mutated by user
   */
  savedGstDetails: GstDetailsFormValues;
  /**
   * Flag indicating if traveller form has been validated successfully.
   * Set to true when validation API returns success.
   * Set to false when any form data changes.
   */
  isTravellerFormValidated: boolean;
  /**
   * Fare summary - updates on addon/meal/baggage/coupon selection
   * Initial values come from template data
   */
  fareSummary: FareSummary;
  /** Current popup to show (e.g., "ALTERNATE_FLOW", "FARE_INCREASED") */
  currentPopup: string | null;
  /** Addon loading state */
  isAddonLoading: boolean;
  /**
   * Version counter that increments when template is updated from chat stream.
   * Used by addon components (coupons, seats, meals, baggage) to trigger re-fetch
   * when the backend sends updated template data (e.g., after applying a coupon via chat).
   */
  templateStreamVersion: number;
  /**
   * Set of traveller IDs whose forms have been validated via the Validate Traveller Form API.
   * Used to skip re-opening the modal for isOpenForm travellers that were already validated.
   * Populated on validateTravellerFormThunk.fulfilled. Preserved across stream updates.
   * Cleared when template is re-initialized via setActiveTemplate. Invalidated when traveller data is edited.
   */
  validatedTravellerIds: string[];
}

/**
 * Main slice state - only contains mutable UI state
 * Template data is passed via props from TemplatePane, not stored here
 */
export interface TemplateReviewTravellerState {
  /**
   * UI state - mutable state for user interactions
   */
  uiState: ReviewTravellerUiState;
}

// ============================================
// Initial State
// ============================================

/** Initial UI state */
export const initialUiState: ReviewTravellerUiState = {
  savedTravellers: {},
  savedContactDetails: EMPTY_CONTACT_DETAILS,
  savedGstDetails: EMPTY_GST_DETAILS,
  isTravellerFormValidated: false,
  fareSummary: {
    reviewPrice: null,
    fareBreakup: null,
  },
  currentPopup: null,
  isAddonLoading: false,
  templateStreamVersion: 0,
  validatedTravellerIds: [],
};

/** Initial slice state */
export const initialTemplateReviewTravellerState: TemplateReviewTravellerState = {
  uiState: initialUiState,
};

// ============================================
// Slice
// ============================================

const templateReviewTravellerSlice = createSlice({
  name: 'flights/templateReviewTraveller',
  initialState: initialTemplateReviewTravellerState,
  reducers: {
    /** Set traveller form validation status */
    setTravellerFormValidated: (state, action: PayloadAction<boolean>) => {
      state.uiState.isTravellerFormValidated = action.payload;
    },

    /** Set fare summary (used when addon selection updates price) */
    setFareSummary: (state, action: PayloadAction<FareSummary>) => {
      state.uiState.fareSummary = action.payload;
    },

    /** Set addon loading state */
    setAddonLoading: (state, action: PayloadAction<boolean>) => {
      state.uiState.isAddonLoading = action.payload;
    },

    /** Clear review popups (after user dismisses them) */
    clearReviewPopups: (state) => {
      state.uiState.currentPopup = null;
    },

    /** Set current popup key to display */
    setCurrentPopup: (state, action: PayloadAction<string | null>) => {
      state.uiState.currentPopup = action.payload;
    },

    /** Reset review traveller state to initial values */
    resetTemplateReviewTravellerState: () => initialTemplateReviewTravellerState,

    // ============================================
    // Saved Travellers Actions (UI State)
    // All actions invalidate the form validation status
    // ============================================

    /** Set entire saved travellers state (used for initialization) */
    setSavedTravellers: (state, action: PayloadAction<SavedTravellersState>) => {
      state.uiState.savedTravellers = action.payload;
      state.uiState.isTravellerFormValidated = false;
    },

    /** Clear saved travellers state */
    clearSavedTravellers: (state) => {
      state.uiState.savedTravellers = {};
      state.uiState.isTravellerFormValidated = false;
    },

    /** Toggle traveller selection (mark as pre-selected or not) */
    toggleSavedTravellerSelection: (
      state,
      action: PayloadAction<{ paxType: PaxType; travellerId: string; isPreSelected: boolean }>,
    ) => {
      const { paxType, travellerId, isPreSelected } = action.payload;
      const travellers = state.uiState.savedTravellers[paxType];
      if (travellers) {
        state.uiState.savedTravellers[paxType] = travellers.map((t) =>
          t.id === travellerId ? { ...t, isPreSelected } : t,
        );
      }
      state.uiState.isTravellerFormValidated = false;
    },

    /** Add new traveller to saved list */
    addSavedTraveller: (
      state,
      action: PayloadAction<{ paxType: PaxType; traveller: TravellerItem }>,
    ) => {
      const { paxType, traveller } = action.payload;
      state.uiState.savedTravellers[paxType] ??= [];
      state.uiState.savedTravellers[paxType].push(traveller);
      state.uiState.isTravellerFormValidated = false;
    },

    /** Update existing traveller in saved list */
    updateSavedTraveller: (
      state,
      action: PayloadAction<{
        paxType: PaxType;
        travellerId: string;
        rowFields: Record<string, string>;
      }>,
    ) => {
      const { paxType, travellerId, rowFields } = action.payload;
      const travellers = state.uiState.savedTravellers[paxType];
      if (travellers) {
        state.uiState.savedTravellers[paxType] = travellers.map((t) =>
          t.id === travellerId ? { ...t, rowFields } : t,
        );
      }
      // Invalidate this traveller's validated status since data changed
      state.uiState.validatedTravellerIds = state.uiState.validatedTravellerIds.filter(
        (id) => id !== travellerId,
      );
      state.uiState.isTravellerFormValidated = false;
    },

    /** Mark a traveller as validated (form was saved via isOpenForm modal flow) */
    markTravellerValidated: (state, action: PayloadAction<string>) => {
      const travellerId = action.payload;
      if (!state.uiState.validatedTravellerIds.includes(travellerId)) {
        state.uiState.validatedTravellerIds.push(travellerId);
      }
    },

    // ============================================
    // Saved Contact Details Actions (UI State)
    // All actions invalidate the form validation status
    // ============================================

    /** Set entire saved contact details */
    setSavedContactDetails: (state, action: PayloadAction<ContactDetailsFormValues>) => {
      state.uiState.savedContactDetails = action.payload;
      state.uiState.isTravellerFormValidated = false;
    },

    /** Update a single field in saved contact details */
    updateSavedContactDetailsField: (
      state,
      action: PayloadAction<{ field: keyof ContactDetailsFormValues; value: string }>,
    ) => {
      const { field, value } = action.payload;
      state.uiState.savedContactDetails[field] = value;
      state.uiState.isTravellerFormValidated = false;
    },

    /** Clear saved contact details */
    clearSavedContactDetails: (state) => {
      state.uiState.savedContactDetails = EMPTY_CONTACT_DETAILS;
      state.uiState.isTravellerFormValidated = false;
    },

    // ============================================
    // Saved GST Details Actions (UI State)
    // All actions invalidate the form validation status
    // ============================================

    /** Set entire saved GST details */
    setSavedGstDetails: (state, action: PayloadAction<GstDetailsFormValues>) => {
      state.uiState.savedGstDetails = action.payload;
      state.uiState.isTravellerFormValidated = false;
    },

    /** Update a single field in saved GST details */
    updateSavedGstDetailsField: (
      state,
      action: PayloadAction<{ field: keyof GstDetailsFormValues; value: string | boolean }>,
    ) => {
      const { field, value } = action.payload;
      if (field === 'optIn') {
        state.uiState.savedGstDetails.optIn = value as boolean;
      } else {
        state.uiState.savedGstDetails[field] = value as string;
      }
      state.uiState.isTravellerFormValidated = false;
    },

    /** Toggle GST opt-in checkbox */
    toggleGstOptIn: (state) => {
      state.uiState.savedGstDetails.optIn = !state.uiState.savedGstDetails.optIn;
      state.uiState.isTravellerFormValidated = false;
    },

    /** Clear saved GST details */
    clearSavedGstDetails: (state) => {
      state.uiState.savedGstDetails = EMPTY_GST_DETAILS;
      state.uiState.isTravellerFormValidated = false;
    },
  },
  extraReducers: (builder) => {
    // ============================================
    // Addon Selection - Updates UI State (fareSummary)
    // ============================================

    // Select Addon
    builder
      .addCase(selectAddonThunk.pending, (state) => {
        state.uiState.isAddonLoading = true;
      })
      .addCase(selectAddonThunk.fulfilled, (state, action) => {
        state.uiState.isAddonLoading = false;
        const responseFareSummary = action.payload.selectionResponse.data.fareSummary;
        if (responseFareSummary) {
          state.uiState.fareSummary = updateFareSummaryFromResponse(
            state.uiState.fareSummary,
            responseFareSummary,
          );
        }
      })
      .addCase(selectAddonThunk.rejected, (state) => {
        state.uiState.isAddonLoading = false;
      });

    // Select Meal (also updates fare summary)
    builder
      .addCase(selectMealThunk.pending, (state) => {
        state.uiState.isAddonLoading = true;
      })
      .addCase(selectMealThunk.fulfilled, (state, action) => {
        state.uiState.isAddonLoading = false;
        const responseFareSummary = action.payload.selectionResponse.data.fareSummary;
        if (responseFareSummary) {
          state.uiState.fareSummary = updateFareSummaryFromResponse(
            state.uiState.fareSummary,
            responseFareSummary,
          );
        }
      })
      .addCase(selectMealThunk.rejected, (state) => {
        state.uiState.isAddonLoading = false;
      });

    // Select Baggage (also updates fare summary)
    builder
      .addCase(selectBaggageThunk.pending, (state) => {
        state.uiState.isAddonLoading = true;
      })
      .addCase(selectBaggageThunk.fulfilled, (state, action) => {
        state.uiState.isAddonLoading = false;
        const responseFareSummary = action.payload.selectionResponse.data.fareSummary;
        if (responseFareSummary) {
          state.uiState.fareSummary = updateFareSummaryFromResponse(
            state.uiState.fareSummary,
            responseFareSummary,
          );
        }
      })
      .addCase(selectBaggageThunk.rejected, (state) => {
        state.uiState.isAddonLoading = false;
      });

    // Select Coupon (also updates fare summary)
    builder
      .addCase(selectCouponThunk.pending, (state) => {
        state.uiState.isAddonLoading = true;
      })
      .addCase(selectCouponThunk.fulfilled, (state, action) => {
        state.uiState.isAddonLoading = false;
        const responseFareSummary = action.payload.selectionResponse.data.fareSummary;
        if (responseFareSummary) {
          state.uiState.fareSummary = updateFareSummaryFromResponse(
            state.uiState.fareSummary,
            responseFareSummary,
          );
        }
      })
      .addCase(selectCouponThunk.rejected, (state) => {
        state.uiState.isAddonLoading = false;
      });

    // Validate Traveller Form
    builder.addCase(validateTravellerFormThunk.fulfilled, (state) => {
      // Validation successful
      state.uiState.isTravellerFormValidated = true;
    });

    // ============================================
    // Initialize UI State from Template Data
    // Template data is passed via props, we only initialize mutable UI state
    // ============================================

    builder.addCase(setActiveTemplate, (state, action) => {
      const { template, data } = action.payload;

      if (!data) {
        return;
      }

      // Handle flights-review-travellers template - initialize UI state
      if (template === 'flights-review-travellers') {
        const uiStateInit = initializeUiStateFromTemplateData(
          data as FlightsReviewTravellersTemplateData,
        );
        state.uiState = uiStateInit;
      }
    });

    // Also handle updateTemplateFromStream (used when template is loaded from chat stream)
    builder.addCase(updateTemplateFromStream, (state, action) => {
      const { activeTemplate, templateData } = action.payload;

      if (!templateData) {
        return;
      }

      // Handle flights-review-travellers template - initialize UI state
      if (activeTemplate === 'flights-review-travellers') {
        // Preserve values that should survive stream updates
        const currentVersion = state.uiState.templateStreamVersion;
        const currentValidatedIds = state.uiState.validatedTravellerIds;

        const uiStateInit = initializeUiStateFromTemplateData(
          templateData as FlightsReviewTravellersTemplateData,
        );
        state.uiState = uiStateInit;
        // Increment version to trigger addon re-fetch (coupons, seats, meals, baggage)
        state.uiState.templateStreamVersion = currentVersion + 1;
        // Preserve validated traveller IDs across stream updates
        state.uiState.validatedTravellerIds = currentValidatedIds;
      }
    });
  },
});

// ============================================
// Actions
// ============================================

export const {
  // UI State Actions
  setTravellerFormValidated,
  setFareSummary,
  setAddonLoading,
  clearReviewPopups,
  setCurrentPopup,
  resetTemplateReviewTravellerState,
  // Saved Travellers Actions (UI State)
  setSavedTravellers,
  clearSavedTravellers,
  toggleSavedTravellerSelection,
  addSavedTraveller,
  updateSavedTraveller,
  markTravellerValidated,
  // Saved Contact Details Actions (UI State)
  setSavedContactDetails,
  updateSavedContactDetailsField,
  clearSavedContactDetails,
  // Saved GST Details Actions (UI State)
  setSavedGstDetails,
  updateSavedGstDetailsField,
  toggleGstOptIn,
  clearSavedGstDetails,
} = templateReviewTravellerSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const templateReviewTravellerReducer = templateReviewTravellerSlice.reducer;
